<template>
    <form
            @submit.prevent="validate"
            v-if="booking.period"
            class="card"
    >
        <booking-summary
                :event="event"
                :booking="booking"
        ></booking-summary>

        <div class="card-body">
            <div class="card-text">
                <h2>
                    {{ $t('details') }}:
                </h2>

                <div class="form-group">
                    <input
                            v-model="booking.email"
                            required
                            type="email"
                            autocomplete="email"
                            class="form-control"
                            id="email"
                            :placeholder="$t('enterEmailAddress')"
                    >
                </div>

                <div class="form-group">
                    <input
                            v-model="booking.email_confirmation"
                            required
                            type="email"
                            autocomplete="email"
                            class="form-control"
                            id="emailConfirmation"
                            :placeholder="$t('confirmEmailAddress')"
                    >
                </div>

                <div
                    v-if="!booking.isFree() && event.sms_service"
                    class="form-check"
                >
                  <hr>

                  <input
                            v-model="booking.send_sms"
                            type="checkbox"
                            class="form-check-input"
                            id="sendSms"
                    >

                    <label
                            for="sendSms"
                            class="form-check-label"
                    >
                        {{ $t('sendSms', [$n(event.sms_price, 'currency')]) }}
                    </label>
                </div>

                <div
                        v-if="booking.send_sms"
                        class="form-group"
                >
                    <label
                            for="telephone"
                            class="sr-only"
                    >
                        {{ $t('telephone') }}
                    </label>

                    <vue-tel-input
                            v-model="booking.telephone"
                            inputId="telephone"
                            mode="international"
                            :preferred-countries="['NL', 'BE', 'DE', 'FR', 'ES']"
                            input-classes="form-control"
                            dynamicPlaceholder
                            required
                            validCharactersOnly
                            @validate="telephoneValidate"
                    ></vue-tel-input>
                </div>

                <hr>

                <i18n
                        :path="agreeTermsKey"
                        tag="small"
                        class="d-inline-block"
                        for="termsAndConditions"
                >
                    <a
                            :href="termsAndConditions"
                            slot="termsAndConditions"
                            target="_blank"
                    >{{ $t('termsAndConditions') }}</a>
                </i18n>

                <hr>
            </div>

            <div class="btn-group btn-block">
                <button
                        type="button"
                        class="btn btn-light"
                        :disabled="saving"
                        @click="$router.go(-1)"
                >
                    {{ $t('back') }}
                </button>

                <button
                        type="submit"
                        class="btn btn-primary bg-primary"
                        :disabled="saving"
                >
                    <template v-if="!saving">
                        {{ $t(submitKey) }}
                    </template>

                    <template v-else>
                        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        {{ $t('loading') }}
                    </template>
                </button>
            </div>
        </div>

        <locker-company-logo></locker-company-logo>
    </form>
</template>

<script>
    import BookingSummary from '../components/BookingSummary';
    import LockerCompanyLogo from '../components/LockerCompanyLogo';
    import eventBus from '../eventBus';

    export default {
        components: {
            BookingSummary,
            LockerCompanyLogo
        },

        props: [
            'event',
            'booking',
            'saving'
        ],

        data() {
            return {
                telephoneValid: false
            }
        },

        computed: {
            totalLockers() {
                return this.booking.totalLockers;
            },

            /**
             * Is payment required for this booking?
             *
             * @returns {Boolean}
             */
            paymentRequired() {
                return this.booking.calculateTotalAmount(this.event) > 0;
            },

            /**
             * Get path to PDF of terms and conditions.
             *
             * @return {String}
             */
            termsAndConditions() {
                let country = 'UK';

                if (this.$i18n.locale === 'nl') {
                    country = 'NL';
                } else if (this.$i18n.locale === 'es') {
                    country = 'ES';
                }

                return `${process.env.BASE_URL}downloads/${country} AGV Locker Company 2024.pdf`;
            },

            /**
             * Get key for text to show as terms.
             *
             * @returns {String}
             */
            agreeTermsKey() {
                if (!this.paymentRequired) {
                    return 'agreeTermsFinish';
                }

                return 'agreeTerms';
            },

            /**
             * Get key for text to show on submit button.
             *
             * @returns {String}
             */
            submitKey() {
                if (!this.paymentRequired) {
                    return 'finish';
                }

                return 'pay';
            }
        },

        watch: {
            '$route': 'init',

            'booking.send_sms': function (value) {
                if (!value) {
                    this.booking.telephone = null;
                }
            }
        },

        created() {
            this.init();
        },

        methods: {
            init() {
                if (!this.booking.period || this.totalLockers === 0) {
                    this.$router.push('/event/' + this.event.code);
                }
            },

            /**
             * Update valid state of telephone.
             *
             * @param {Object} e
             */
            telephoneValidate(e) {
                this.telephoneValid = e.valid;
            },

            /**
             * Validate this form, if valid emit event 'ready' and go to next page.
             */
            validate() {
                eventBus.$emit('clearErrors');

                if (!this.booking.email || !this.booking.email_confirmation) {
                    eventBus.$emit('error', this.$t('errors.noEmail'));
                    return;
                }

                if (this.booking.email !== this.booking.email_confirmation) {
                    eventBus.$emit('error', this.$t('errors.emailNoMatch'));
                    return;
                }

                if (this.booking.send_sms && (!this.booking.telephone || !this.telephoneValid)) {
                    eventBus.$emit('error', this.$t('errors.noTelephone'));
                    return;
                }

                this.$emit('ready', this.booking, true);
            }
        }
    }
</script>
